// Approved #05/#22 editor behavior. These are explicitly local preview facts.
// A production adapter must supply authorized work context and verified plans;
// neither a browser request nor an agreement checkbox authorizes a provider write.
import type { GroupKey } from './mockData.js';
import type { ReviewReturnTarget } from '../_shared/ReviewState.js';

export type ProductKey = 'gut' | 'osteo' | 'hydra';
export type AddressKey = 'barn' | 'home';
export type PlanStatus = 'active' | 'paused' | 'cancelled';
export type SubscriptionAction = 'date' | 'frequency' | 'products' | 'skip' | 'pause' | 'resume' | 'reactivate' | 'cancel' | 'address';
export type SubscriptionDialogAction = SubscriptionAction | 'payment';

export const PRODUCTS: Record<ProductKey, { name: string; code: 'GX' | 'OM' | 'HM'; price: number }> = {
  gut: { name: 'Gut X', code: 'GX', price: 105 },
  osteo: { name: 'Osteo-MAX', code: 'OM', price: 90 },
  hydra: { name: 'Hydra-MAX', code: 'HM', price: 90 },
};
export const ADDRESSES: Record<AddressKey, string> = {
  barn: 'Cedar Creek barn · Nashville, TN',
  home: 'Home · Nashville, TN',
};
export const ACTION_NAMES: Record<SubscriptionDialogAction, string> = {
  date: 'Next order date', frequency: 'Delivery frequency', products: 'Products & quantities',
  skip: 'Skip next order', pause: 'Pause subscription', resume: 'Resume subscription',
  reactivate: 'Reactivate subscription', cancel: 'Cancel subscription', address: 'Shipping address',
  payment: 'Update payment method',
};
export const STATUS_NAMES: Record<PlanStatus, string> = { active: 'Active', paused: 'Paused', cancelled: 'Cancelled' };
export const PREVIEW_MIN_DATE = '2026-10-10';

export interface PlanValues {
  id: GroupKey;
  name: string;
  status: PlanStatus;
  nextDate: string;
  cadence: number;
  address: AddressKey;
  products: { product: ProductKey; quantity: number }[];
}
export interface SubscriptionPlan extends PlanValues {
  revision: number;
  history: SubscriptionHistory[];
}
export interface SubscriptionHistory {
  id: string;
  action: SubscriptionAction;
  at: string;
  before: PlanValues;
  after: PlanValues;
  reason: string;
  note: string;
  source: 'preview';
}
export interface SubscriptionInput {
  date?: string;
  reason?: string;
  note?: string;
  cadence?: string;
  address?: AddressKey;
  products?: { product: ProductKey; quantity: string }[];
}
export interface SubscriptionDraft {
  customerId: string;
  planId: GroupKey;
  action: SubscriptionAction;
  revision: number;
  base: SubscriptionInput;
  input: SubscriptionInput;
  agreedTerms: string | null;
}
export interface SubscriptionTerms {
  before: PlanValues;
  after: PlanValues;
  reason: string;
  note: string;
  merchandiseBefore: number;
  merchandiseAfter: number;
}
export interface SubscriptionChangeRequest {
  operationId: string;
  customerId: string;
  planId: GroupKey;
  action: SubscriptionAction;
  expectedRevision: number;
  input: SubscriptionInput;
  terms: SubscriptionTerms;
  termsKey: string;
  agreed: true;
}
interface ReceiptBinding {
  operationId: string;
  customerId: string;
  planId: GroupKey;
  action: SubscriptionAction;
  expectedRevision: number;
  termsKey: string;
}
export type SubscriptionReceipt = ReceiptBinding & (
  | { outcome: 'confirmed'; plan: PlanValues & { revision: number }; at: string }
  | { outcome: 'pending' | 'unknown' | 'failed'; message?: string }
);
export interface SubscriptionChangeAdapter {
  // Preview interaction seam. Network/runtime implementations must enforce
  // their own verified authority, quote and provider-result contracts.
  submit(request: SubscriptionChangeRequest): Promise<SubscriptionReceipt>;
  check(request: SubscriptionChangeRequest): Promise<SubscriptionReceipt>;
}
export interface PendingSubscriptionChange {
  request: SubscriptionChangeRequest;
  phase: 'pending' | 'unknown';
  returnAfter: boolean;
  returnTarget: ReviewReturnTarget;
}
export interface SubscriptionReviewState {
  customerId: string;
  selected: GroupKey;
  plans: Record<GroupKey, SubscriptionPlan>;
  drafts: Record<string, SubscriptionDraft>;
  pending: PendingSubscriptionChange | null;
  message: string;
  error: boolean;
}

export const copy = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
export const draftKey = (planId: GroupKey, action: SubscriptionAction): string => planId + ':' + action;
export function createSubscriptionReviewState(customerId: string): SubscriptionReviewState {
  return { customerId, selected: 'barn', drafts: {}, pending: null, message: '', error: false, plans: {
    barn: { id: 'barn', name: 'Barn supplements', revision: 1, status: 'active', nextDate: '2026-10-15', cadence: 60, address: 'barn', products: [{ product: 'gut', quantity: 2 }, { product: 'osteo', quantity: 1 }], history: [] },
    hydration: { id: 'hydration', name: 'Hydration', revision: 1, status: 'active', nextDate: '2026-10-28', cadence: 30, address: 'home', products: [{ product: 'hydra', quantity: 1 }], history: [] },
  } };
}
export function validDate(value: string | undefined): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
  const date = new Date(value + 'T12:00:00Z');
  return Number.isFinite(date.valueOf()) && date.toISOString().slice(0, 10) === value;
}
export function addDays(value: string, amount: number): string {
  const date = new Date(value + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}
export function displayDate(value: string, short = false): string {
  if (!validDate(value)) return 'Choose a date';
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: short ? 'short' : 'long', day: 'numeric', year: 'numeric' }).format(new Date(value + 'T12:00:00Z'));
}
export const merchandiseTotal = (plan: PlanValues): number => plan.products.reduce((sum, p) => sum + (PRODUCTS[p.product]?.price || 0) * p.quantity, 0);
export const productSummary = (plan: PlanValues): string => plan.products.map(p => PRODUCTS[p.product].name).join(' + ');
export function describePlanValue(plan: PlanValues, action: SubscriptionAction): string {
  if (action === 'products') return plan.products.map(p => PRODUCTS[p.product].name + ' × ' + p.quantity).join(', ');
  if (action === 'frequency') return 'Every ' + plan.cadence + ' days';
  if (action === 'address') return ADDRESSES[plan.address];
  if (['pause', 'resume', 'reactivate', 'cancel'].includes(action)) return plan.status === 'paused' ? 'Paused until ' + displayDate(plan.nextDate, true) : STATUS_NAMES[plan.status];
  return displayDate(plan.nextDate);
}
export function planValues(plan: PlanValues): PlanValues {
  return copy({ id: plan.id, name: plan.name, status: plan.status, nextDate: plan.nextDate, cadence: plan.cadence, address: plan.address, products: plan.products });
}
function initialInput(plan: SubscriptionPlan, action: SubscriptionAction): SubscriptionInput {
  switch (action) {
    case 'date': return { date: plan.nextDate, reason: '', note: '' };
    case 'frequency': return { cadence: String(plan.cadence), date: plan.nextDate };
    case 'products': return { products: plan.products.map(p => ({ product: p.product, quantity: String(p.quantity) })) };
    case 'address': return { address: plan.address };
    case 'pause': return { date: '', note: '' };
    case 'resume': case 'reactivate': return { date: plan.nextDate };
    case 'cancel': return { reason: '', note: '' };
    case 'skip': return {};
  }
}
export function getDraft(state: SubscriptionReviewState, planId: GroupKey, action: SubscriptionAction): SubscriptionDraft {
  const existing = state.drafts[draftKey(planId, action)];
  if (existing) return existing;
  const plan = state.plans[planId], input = initialInput(plan, action);
  return { customerId: state.customerId, planId, action, revision: plan.revision, base: copy(input), input, agreedTerms: null };
}
export function ensureDraft(state: SubscriptionReviewState, planId: GroupKey, action: SubscriptionAction): SubscriptionReviewState {
  if (state.pending) return state;
  return { ...state, selected: planId, drafts: { ...state.drafts, [draftKey(planId, action)]: getDraft(state, planId, action) }, message: '', error: false };
}
export const isDraftDirty = (draft: SubscriptionDraft): boolean => JSON.stringify(draft.input) !== JSON.stringify(draft.base) || draft.agreedTerms !== null;
export function allowedAction(plan: SubscriptionPlan, action: SubscriptionAction): boolean {
  if (plan.status === 'cancelled') return action === 'reactivate' || action === 'address';
  if (plan.status === 'paused') return ['resume', 'cancel', 'address', 'products'].includes(action);
  return ['date', 'frequency', 'products', 'skip', 'pause', 'cancel', 'address'].includes(action);
}
export function draftTerms(state: SubscriptionReviewState, draft: SubscriptionDraft): SubscriptionTerms {
  const before = planValues(state.plans[draft.planId]), after = copy(before), input = draft.input;
  switch (draft.action) {
    case 'date': case 'resume': case 'reactivate':
      after.nextDate = input.date || '';
      if (draft.action !== 'date') after.status = 'active';
      break;
    case 'frequency': after.cadence = Number(input.cadence); after.nextDate = input.date || ''; break;
    case 'products': after.products = (input.products || []).map(p => ({ product: p.product, quantity: Number(p.quantity) })); break;
    case 'skip': after.nextDate = addDays(before.nextDate, before.cadence); break;
    case 'pause': after.status = 'paused'; after.nextDate = input.date || ''; break;
    case 'cancel': after.status = 'cancelled'; break;
    case 'address': after.address = input.address || before.address; break;
  }
  return { before, after, reason: input.reason || '', note: input.note || '', merchandiseBefore: merchandiseTotal(before), merchandiseAfter: merchandiseTotal(after) };
}
export function termsKey(state: SubscriptionReviewState, draft: SubscriptionDraft): string {
  return JSON.stringify({ customerId: state.customerId, planId: draft.planId, action: draft.action, revision: draft.revision, input: draft.input, terms: draftTerms(state, draft) });
}
export function draftError(state: SubscriptionReviewState, draft: SubscriptionDraft, requireAgreement = true): string {
  const plan = state.plans[draft.planId];
  if (!plan || draft.customerId !== state.customerId || draft.planId !== state.selected) return 'The original customer or subscription changed. Reopen this change.';
  if (plan.revision !== draft.revision) return 'This subscription changed. Discard this draft and review the latest details.';
  if (!allowedAction(plan, draft.action)) return 'This action is unavailable for the selected subscription.';
  const input = draft.input;
  if (['date', 'frequency', 'pause', 'resume', 'reactivate'].includes(draft.action)) {
    if (!validDate(input.date)) return 'Choose a complete, valid order-processing date.';
    if ((input.date || '') < PREVIEW_MIN_DATE) return 'Choose a future order-processing date in this example.';
  }
  if (draft.action === 'date' && !['more_supply', 'customer_schedule', 'other'].includes(input.reason || '')) return 'Choose the reason Sarah gave for changing the date.';
  if (draft.action === 'cancel' && !['no_longer_needed', 'customer_request', 'other'].includes(input.reason || '')) return 'Choose the reason Sarah gave for cancelling.';
  if (input.reason === 'other' && !input.note?.trim()) return 'Add Sarah’s reason in the note.';
  if (draft.action === 'frequency' && ![30, 60, 90].includes(Number(input.cadence))) return 'Choose an available delivery interval.';
  if (draft.action === 'address' && !Object.hasOwn(ADDRESSES, input.address || '')) return 'Choose an existing saved address.';
  if (draft.action === 'products') {
    if (!input.products?.length) return 'Keep at least one product, or use Cancel subscription.';
    const seen = new Set<ProductKey>();
    for (const product of input.products) {
      const quantity = Number(product.quantity);
      if (!Object.hasOwn(PRODUCTS, product.product) || !product.quantity.trim() || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 10000) return 'Use a listed product and a whole quantity of at least one.';
      if (seen.has(product.product)) return 'This product is already on the plan. Change its quantity instead.';
      seen.add(product.product);
    }
  }
  if (JSON.stringify(planValues(plan)) === JSON.stringify(draftTerms(state, draft).after)) return 'Make a requested change before saving.';
  if (requireAgreement && draft.agreedTerms !== termsKey(state, draft)) return 'Confirm Sarah agreed to these exact details.';
  return '';
}
export function editDraft(state: SubscriptionReviewState, planId: GroupKey, action: SubscriptionAction, input: SubscriptionInput): SubscriptionReviewState {
  if (state.pending) return state;
  const draft = getDraft(state, planId, action);
  return { ...state, drafts: { ...state.drafts, [draftKey(planId, action)]: { ...draft, input: copy(input), agreedTerms: null } }, message: '', error: false };
}
export function agreeToDraft(state: SubscriptionReviewState, planId: GroupKey, action: SubscriptionAction, agreed: boolean): SubscriptionReviewState {
  if (state.pending) return state;
  const draft = getDraft(state, planId, action);
  return { ...state, drafts: { ...state.drafts, [draftKey(planId, action)]: { ...draft, agreedTerms: agreed && !draftError(state, draft, false) ? termsKey(state, draft) : null } } };
}
export function discardDraft(state: SubscriptionReviewState, planId: GroupKey, action: SubscriptionAction): SubscriptionReviewState {
  if (state.pending) return state;
  const drafts = { ...state.drafts };
  delete drafts[draftKey(planId, action)];
  return { ...state, drafts, message: 'Unsaved subscription edits discarded.', error: false };
}
export function createChangeRequest(state: SubscriptionReviewState, draft: SubscriptionDraft, operationId: string): SubscriptionChangeRequest {
  if (state.pending) throw new Error('Check the existing save result before another change.');
  const error = draftError(state, draft);
  if (error) throw new Error(error);
  return { operationId, customerId: state.customerId, planId: draft.planId, action: draft.action, expectedRevision: draft.revision, input: copy(draft.input), terms: draftTerms(state, draft), termsKey: termsKey(state, draft), agreed: true };
}
export function receiptMatches(request: SubscriptionChangeRequest, receipt: SubscriptionReceipt): boolean {
  return Boolean(receipt && receipt.operationId === request.operationId && receipt.customerId === request.customerId && receipt.planId === request.planId && receipt.action === request.action && receipt.expectedRevision === request.expectedRevision && receipt.termsKey === request.termsKey);
}
export function isConfirmedReceipt(request: SubscriptionChangeRequest, receipt: SubscriptionReceipt): boolean {
  return receiptMatches(request, receipt) && receipt.outcome === 'confirmed' && Boolean(receipt.plan) && Number.isSafeInteger(receipt.plan.revision) && receipt.plan.revision > request.expectedRevision && Number.isFinite(Date.parse(receipt.at)) && JSON.stringify(planValues(receipt.plan)) === JSON.stringify(request.terms.after);
}
export function unknownChange(state: SubscriptionReviewState, request: SubscriptionChangeRequest, message = 'The save result is unknown. Check this same request before changing or saving again.'): SubscriptionReviewState {
  if (state.customerId !== request.customerId || state.pending?.request.operationId !== request.operationId) return state;
  return { ...state, pending: { ...state.pending, phase: 'unknown' }, message, error: true };
}
export function applyReceipt(state: SubscriptionReviewState, request: SubscriptionChangeRequest, receipt: SubscriptionReceipt): SubscriptionReviewState {
  if (state.customerId !== request.customerId || state.pending?.request.operationId !== request.operationId) return state;
  if (!receiptMatches(request, receipt)) return unknownChange(state, request, 'The result does not match the original save. Your draft and original request are retained.');
  const plan = state.plans[request.planId];
  if (plan.revision !== request.expectedRevision) return unknownChange(state, request, 'This plan changed while the save was waiting. Check the original request; no newer details were overwritten.');
  if (receipt.outcome === 'unknown') return unknownChange(state, request);
  if (receipt.outcome === 'pending') return { ...state, pending: { ...state.pending, phase: 'pending' }, message: 'The save is still pending. Check the same request; the plan has not changed.', error: false };
  if (receipt.outcome === 'failed') return { ...state, pending: null, message: receipt.message || 'The save failed. Your edits remain available for review.', error: true };
  if (receipt.outcome !== 'confirmed') return unknownChange(state, request);
  if (!isConfirmedReceipt(request, receipt)) return unknownChange(state, request, 'The confirmed details do not match the reviewed change. Check the original request before continuing.');
  const event: SubscriptionHistory = { id: request.operationId, action: request.action, at: receipt.at, before: copy(request.terms.before), after: copy(request.terms.after), reason: request.terms.reason, note: request.terms.note, source: 'preview' };
  const drafts = { ...state.drafts };
  delete drafts[draftKey(request.planId, request.action)];
  return { ...state, plans: { ...state.plans, [request.planId]: { ...planValues(receipt.plan), revision: receipt.plan.revision, history: [event, ...plan.history] } }, drafts, pending: null, message: 'Saved in this preview · ' + plan.name + ' · Jamie Dorsey', error: false };
}
export function confirmedPreviewReceipt(request: SubscriptionChangeRequest): SubscriptionReceipt {
  return { operationId: request.operationId, customerId: request.customerId, planId: request.planId, action: request.action, expectedRevision: request.expectedRevision, termsKey: request.termsKey, outcome: 'confirmed', plan: { ...copy(request.terms.after), revision: request.expectedRevision + 1 }, at: new Date().toISOString() };
}
export const previewSubscriptionAdapter: SubscriptionChangeAdapter = {
  submit: async request => confirmedPreviewReceipt(request),
  // An unresolved preview request is not promoted to confirmed on a check.
  check: async request => ({ operationId: request.operationId, customerId: request.customerId, planId: request.planId, action: request.action, expectedRevision: request.expectedRevision, termsKey: request.termsKey, outcome: 'unknown' }),
};
export function finishReason(state: SubscriptionReviewState): string {
  if (state.pending) return 'Check the existing save result before finishing.';
  if (Object.values(state.drafts).some(isDraftDirty)) return 'Save or discard the open subscription edits before finishing.';
  return 'Subscription review · Preview only';
}

// ---------------------------------------------------------------------------
// W35 (2026-10-20): read-only display helpers for the REAL subscription
// summary (GET /v1/contacts/:id/subscription-summary, W34 sink; the
// typed shape lives in screens/_shared/useSubscriptionSummary.ts).
//
// Pure functions. Nothing here feeds the preview state machine above and
// nothing above is used to invent a value for a real contact: a null input
// becomes an explicit "not on file" label, never a sample.
// ---------------------------------------------------------------------------

/** Label on every inert Recharge control on a real session. */
export const RECHARGE_GATE_NOTE = 'Recharge changes are paused by the live-write gate';

// Recharge `payment_type` enumeration labels (READ from the Recharge API
// reference: CREDIT_CARD, PAYPAL, APPLE_PAY, GOOGLE_PAY, SEPA_DEBIT,
// SHOP_PAY). Any other value is title-cased rather than guessed.
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  SHOP_PAY: 'Shop Pay',
  CREDIT_CARD: 'Credit card',
  PAYPAL: 'PayPal',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  SEPA_DEBIT: 'SEPA debit',
};

/** 'queued' -> 'Queued'; 'SHOP_PAY' -> 'Shop Pay'. */
export function titleCaseToken(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function describePaymentType(type: string | null): string {
  if (!type) return 'Payment type not on file';
  return PAYMENT_TYPE_LABELS[type.toUpperCase()] ?? titleCaseToken(type);
}

/** Short badge text for the payment card ("SHOP", "CARD", "PAYPAL"). */
export function paymentBadge(type: string | null): string {
  if (!type) return 'N/A';
  const upper = type.toUpperCase();
  if (upper === 'CREDIT_CARD') return 'CARD';
  const first = upper.split(/[\s_-]+/).filter(Boolean)[0] ?? upper;
  return first.slice(0, 6);
}

export function describePaymentExpiry(expMonth: number | null, expYear: number | null): string {
  if (expMonth === null || expYear === null) return 'Expiry not on file';
  return `Expires ${String(expMonth).padStart(2, '0')}/${expYear}`;
}

export function describePaymentValidity(valid: boolean | null): string {
  if (valid === true) return 'Valid payment method on file';
  if (valid === false) return 'Payment method needs attention';
  return 'Payment validity not reported';
}

export function describeCadence(frequencyDays: number | null, frequencyLabel: string | null): string {
  if (frequencyDays !== null && Number.isFinite(frequencyDays) && frequencyDays > 0) {
    return `Every ${frequencyDays} day${frequencyDays === 1 ? '' : 's'}`;
  }
  if (frequencyLabel) return frequencyLabel;
  return 'Cadence not on file';
}

export function describeChargeStatus(chargeStatus: string | null): string {
  return chargeStatus ? titleCaseToken(chargeStatus) : 'Charge status not on file';
}

/** '$294.45', '$3,804.07'; null when the amount is not on file. */
export function formatUsdAmount(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Calendar tile for a YYYY-MM-DD date: { month: 'JAN', day: '18' }; null when invalid. */
export function calendarTileFor(ymd: string | null): { month: string; day: string } | null {
  if (!ymd || !validDate(ymd)) return null;
  return {
    month: new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(new Date(ymd + 'T12:00:00Z')).toUpperCase(),
    day: ymd.slice(-2),
  };
}

/** 'January 18, 2027' for a YYYY-MM-DD date; 'Not on file' otherwise. */
export function describeSummaryDate(ymd: string | null): string {
  if (!ymd || !validDate(ymd)) return 'Not on file';
  return displayDate(ymd);
}

/** '0 points · No tier on file' / '120 points · Gold'; null when neither is on file. */
export function describeLoyalty(points: number | null, tier: string | null): string | null {
  if (points === null && tier === null) return null;
  const pointsLabel = points === null ? 'Points not on file' : `${points.toLocaleString('en-US')} point${points === 1 ? '' : 's'}`;
  const tierLabel = tier ? tier : 'No tier on file';
  return `${pointsLabel} · ${tierLabel}`;
}

/** Status pill text: the HubSpot label when present, else the Recharge status title-cased. */
export function describeSubscriptionStatus(status: string, subscriptionStatusLabel: string | null): string {
  if (subscriptionStatusLabel) return subscriptionStatusLabel;
  return titleCaseToken(status);
}
